/**
 * Utilidades para formateo de datos según configuración del usuario
 */

/**
 * Formatea una fecha según el formato especificado en la configuración
 * @param {Date|string} date - La fecha a formatear
 * @param {string} format - El formato deseado (DD/MM/YYYY, MM/DD/YYYY, YYYY-MM-DD)
 * @returns {string} - La fecha formateada
 */
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return 'N/A';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return 'Fecha inválida';
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  switch (format) {
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Formatea un monto monetario según el formato especificado
 * @param {number|string} amount - El monto a formatear
 * @param {string} format - El formato deseado ($0,0.00, $0.0,00, 0,0.00)
 * @returns {string} - El monto formateado
 */
export const formatCurrency = (amount, format = '$0,0.00') => {
  if (amount === null || amount === undefined) return '$0.00';
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) return '$0.00';
  
  const absAmount = Math.abs(numAmount);
  const isNegative = numAmount < 0;
  
  // Separar parte entera y decimal
  const parts = absAmount.toFixed(2).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  let formattedAmount = '';
  
  switch (format) {
    case '$0.0,00':
      // Formato europeo: 1.234,56
      formattedAmount = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      formattedAmount = `$${formattedAmount},${decimalPart}`;
      break;
      
    case '0,0.00':
      // Sin símbolo de moneda: 1,234.56
      formattedAmount = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedAmount = `${formattedAmount}.${decimalPart}`;
      break;
      
    case '$0,0.00':
    default:
      // Formato americano estándar: $1,234.56
      formattedAmount = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedAmount = `$${formattedAmount}.${decimalPart}`;
      break;
  }
  
  return isNegative ? `-${formattedAmount}` : formattedAmount;
};

/**
 * Formatea una fecha y hora completa
 * @param {Date|string} dateTime - La fecha/hora a formatear
 * @param {string} dateFormat - El formato de fecha deseado
 * @returns {string} - La fecha y hora formateadas
 */
export const formatDateTime = (dateTime, dateFormat = 'DD/MM/YYYY') => {
  if (!dateTime) return 'N/A';
  
  const dt = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  if (isNaN(dt.getTime())) return 'Fecha inválida';
  
  const datePart = formatDate(dt, dateFormat);
  const hours = String(dt.getHours()).padStart(2, '0');
  const minutes = String(dt.getMinutes()).padStart(2, '0');
  
  return `${datePart} ${hours}:${minutes}`;
};

/**
 * Formatea un número de teléfono
 * @param {string} phone - El número de teléfono
 * @returns {string} - El teléfono formateado
 */
export const formatPhone = (phone) => {
  if (!phone) return 'N/A';
  
  // Eliminar todos los caracteres no numéricos
  const cleaned = phone.toString().replace(/\D/g, '');
  
  // Formato: (123) 456-7890 para números de 10 dígitos
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Formato internacional: +52 123 456 7890
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, cleaned.length - 10)} ${cleaned.slice(-10, -7)} ${cleaned.slice(-7, -4)} ${cleaned.slice(-4)}`;
  }
  
  return phone; // Retornar original si no coincide con formatos conocidos
};

/**
 * Formatea un RFC (Registro Federal de Contribuyentes)
 * @param {string} rfc - El RFC
 * @returns {string} - El RFC formateado en mayúsculas
 */
export const formatRFC = (rfc) => {
  if (!rfc) return 'N/A';
  return rfc.toString().toUpperCase().trim();
};

/**
 * Formatea un porcentaje
 * @param {number} value - El valor del porcentaje
 * @param {number} decimals - Número de decimales (default: 2)
 * @returns {string} - El porcentaje formateado
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined) return '0%';
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '0%';
  return `${numValue.toFixed(decimals)}%`;
};

/**
 * Formatea un número de recibo/factura con prefijo
 * @param {string|number} number - El número del recibo
 * @param {string} prefix - El prefijo (ej: 'INV-', 'REC-')
 * @returns {string} - El número formateado
 */
export const formatReceiptNumber = (number, prefix = '') => {
  if (!number) return 'N/A';
  return prefix ? `${prefix}${number}` : number.toString();
};

/**
 * Calcula y formatea la diferencia de días entre dos fechas
 * @param {Date|string} date1 - Primera fecha
 * @param {Date|string} date2 - Segunda fecha (default: hoy)
 * @returns {number} - Número de días de diferencia
 */
export const getDaysDifference = (date1, date2 = new Date()) => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;
  
  const diffTime = d2.getTime() - d1.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Formatea un estado de pago con colores
 * @param {string} status - El estado del pago
 * @returns {object} - Objeto con color y texto formateado
 */
export const formatPaymentStatus = (status) => {
  const estados = {
    'pagado': { color: '#4CAF50', text: 'Pagado', bgColor: '#4CAF5015' },
    'pendiente': { color: '#FFC107', text: 'Pendiente', bgColor: '#FFC10715' },
    'parcial': { color: '#FF9800', text: 'Parcial', bgColor: '#FF980015' },
    'vencido': { color: '#F44336', text: 'Vencido', bgColor: '#F4433615' },
    'cancelado': { color: '#9E9E9E', text: 'Cancelado', bgColor: '#9E9E9E15' }
  };
  
  return estados[status?.toLowerCase()] || { 
    color: '#757575', 
    text: status || 'Desconocido',
    bgColor: '#75757515'
  };
};

/**
 * Trunca un texto a una longitud específica
 * @param {string} text - El texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} - Texto truncado con "..."
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Valida y formatea un email
 * @param {string} email - El email a validar
 * @returns {string} - Email en minúsculas o 'N/A'
 */
export const formatEmail = (email) => {
  if (!email) return 'N/A';
  return email.toString().toLowerCase().trim();
};

export default {
  formatDate,
  formatCurrency,
  formatDateTime,
  formatPhone,
  formatRFC,
  formatPercentage,
  formatReceiptNumber,
  getDaysDifference,
  formatPaymentStatus,
  truncateText,
  formatEmail
};
